import {
  VehicleType,
  VehicleAreasMap,
  AreaPosition,
  AreaDefinition,
  VehicleView,
} from './VehiclePaintMeasurement.types';

// Importy komponentów SVG dla sedana
import { AreaA_Sedan_Left } from './areas/AreaA_Sedan_Left';
import { AreaB_Sedan_Left } from './areas/AreaB_Sedan_Left';
import { AreaC_Sedan_Left } from './areas/AreaC_Sedan_Left';
import { AreaD_Sedan_Left } from './areas/AreaD_Sedan_Left';
import { AreaA_Sedan_Right } from './areas/AreaA_Sedan_Right';
import { AreaB_Sedan_Right } from './areas/AreaB_Sedan_Right';
import { AreaC_Sedan_Right } from './areas/AreaC_Sedan_Right';
import { AreaD_Sedan_Right } from './areas/AreaD_Sedan_Right';
import { AreaA_Sedan_Top } from './areas/AreaA_Sedan_Top';
import { AreaB_Sedan_Top } from './areas/AreaB_Sedan_Top';
import { AreaC_Sedan_Top } from './areas/AreaC_Sedan_Top';

// Importy komponentów SVG dla hatchback
import { AreaA_Hatchback_Left } from './areas/AreaA_Hatchback_Left';
import { AreaB_Hatchback_Left } from './areas/AreaB_Hatchback_Left';
import { AreaC_Hatchback_Left } from './areas/AreaC_Hatchback_Left';
import { AreaA_Hatchback_Center } from './areas/AreaA_Hatchback_Center';
import { AreaB_Hatchback_Center } from './areas/AreaB_Hatchback_Center';
import { AreaC_Hatchback_Center } from './areas/AreaC_Hatchback_Center';
import { AreaA_Hatchback_Right } from './areas/AreaA_Hatchback_Right';
import { AreaB_Hatchback_Right } from './areas/AreaB_Hatchback_Right';
import { AreaC_Hatchback_Right } from './areas/AreaC_Hatchback_Right';

// Importy komponentów SVG dla van
import { AreaA_Van_Left } from './areas/AreaA_Van_Left';
import { AreaB_Van_Left } from './areas/AreaB_Van_Left';
import { AreaC_Van_Left } from './areas/AreaC_Van_Left';
import { AreaA_Van_Center } from './areas/AreaA_Van_Center';
import { AreaB_Van_Center } from './areas/AreaB_Van_Center';
import { AreaC_Van_Center } from './areas/AreaC_Van_Center';
import { AreaD_Van_Center } from './areas/AreaD_Van_Center';
import { AreaA_Van_Right } from './areas/AreaA_Van_Right';
import { AreaB_Van_Right } from './areas/AreaB_Van_Right';
import { AreaC_Van_Right } from './areas/AreaC_Van_Right';
import { AreaD_Van_Right } from './areas/AreaD_Van_Right';

/**
 * Mapowanie obszarów lakieru dla SEDAN
 *
 * INSTRUKCJE:
 * 1. Skopiuj plik przykładowy `areas/AreaA_Example.tsx`
 * 2. Zmień nazwę na np. `AreaA_Sedan_Left.tsx`
 * 3. Zamień pathData, viewBox, width, height na dane z Twojego SVG
 * 4. Zaimportuj komponent powyżej
 * 5. Dodaj do mapy z pozycjonowaniem
 *
 * Przykład:
 * ```typescript
 * import { AreaA_Sedan_Left } from './areas/AreaA_Sedan_Left';
 *
 * const sedanAreasMap: VehicleAreasMap = {
 *   left: {
 *     A: {
 *       SVGComponent: AreaA_Sedan_Left,
 *       positionX: 20,    // Gdzie obszar ma być na widoku pojazdu (0-100%)
 *       positionY: 40,    // Gdzie obszar ma być na widoku pojazdu (0-100%)
 *       labelX: 50,       // Gdzie etykieta ma być na obszarze (0-100%)
 *       labelY: 50,       // Gdzie etykieta ma być na obszarze (0-100%)
 *     },
 *     // ... inne obszary
 *   },
 *   // ... top, right
 * };
 * ```
 *
 * POZYCJONOWANIE:
 * - positionX, positionY - pozycja CAŁEGO obszaru SVG na widoku pojazdu (0-100%)
 * - labelX, labelY - pozycja ETYKIETY (litery + wartości) na obszarze SVG (0-100%)
 *   Zazwyczaj labelX: 50, labelY: 50 (środek obszaru)
 *
 * UWAGA: Obecnie wszystkie obszary są ustawione na undefined.
 * Musisz dodać importy i definicje dla każdego obszaru, który ma być wyświetlany.
 */
const sedanAreasMap: VehicleAreasMap = {
  left: {
    1: {
      SVGComponent: AreaA_Sedan_Left,
      positionX: 43,
      positionY: 20.9,
      labelX: 50,
      labelY: 80,
    },
    3: {
      SVGComponent: AreaB_Sedan_Left,
      positionX: 43.2,
      positionY: 43,
      labelX: 50,
      labelY: 50,
    },
    5: {
      SVGComponent: AreaC_Sedan_Left,
      positionX: 45.4,
      positionY: 63.5,
      labelX: 50,
      labelY: 50,
    },
    6: {
      SVGComponent: AreaD_Sedan_Left,
      positionX: 57.4,
      positionY: 83.3,
      labelX: 50,
      labelY: 55,
    },
  },
  top: {
    27: {
      SVGComponent: AreaA_Sedan_Top,
      positionX: 50.2,
      positionY: 14.7,
      labelX: 50,
      labelY: 50,
      displayValue: 'Wzmocnienie boczne',
    },
    26: {
      SVGComponent: AreaB_Sedan_Top,
      positionX: 50,
      positionY: 60,
      labelX: 50,
      labelY: 50,
      displayValue: 'Wzmocnienie boczne',
    },
    '13': {
      SVGComponent: AreaC_Sedan_Top,
      positionX: 50,
      positionY: 93.6,
      labelX: 50,
      labelY: 50,
      displayValue: 'Wzmocnienie boczne',
    },
  },
  right: {
    20: {
      SVGComponent: AreaA_Sedan_Right,
      positionX: 57.8,
      positionY: 20.8,
      labelX: 50,
      labelY: 80,
    },
    18: {
      SVGComponent: AreaB_Sedan_Right,
      positionX: 57.8,
      positionY: 42.1,
      labelX: 50,
      labelY: 50,
    },
    16: {
      SVGComponent: AreaC_Sedan_Right,
      positionX: 54.4,
      positionY: 63,
      labelX: 50,
      labelY: 50,
    },
    14: {
      SVGComponent: AreaD_Sedan_Right,
      positionX: 42.7,
      positionY: 82.8,
      labelX: 50,
      labelY: 55,
    },
  },
};

/**
 * Mapowanie obszarów lakieru dla VAN
 *
 * Van ma:
 * - left: 3 obszary (A, B, C)
 * - top (center): 4 obszary (A, B, C, D)
 * - right: 4 obszary (A, B, C, D)
 */
const vanAreasMap: VehicleAreasMap = {
  left: {
    1: {
      SVGComponent: AreaA_Van_Left,
      positionX: 39.5,
      positionY: 19.6,
      labelX: 50,
      labelY: 70,
    },
    3: {
      SVGComponent: AreaB_Van_Left,
      positionX: 39.2,
      positionY: 38,
      labelX: 50,
      labelY: 50,
    },
    5: {
      SVGComponent: AreaC_Van_Left,
      positionX: 57.2,
      positionY: 70.8,
      labelX: 50,
      labelY: 50,
    },
  },
  top: {
    28: {
      SVGComponent: AreaA_Van_Center,
      positionX: 50,
      positionY: 12.7,
      labelX: 50,
      labelY: 50,
    },
    29: {
      SVGComponent: AreaB_Van_Center,
      positionX: 50,
      positionY: 63.6,
      labelX: 50,
      labelY: 50,
    },
    9: {
      SVGComponent: AreaC_Van_Center,
      positionX: 34,
      positionY: 95.9,
      labelX: 50,
      labelY: 50,
    },
    10: {
      SVGComponent: AreaD_Van_Center,
      positionX: 65.5,
      positionY: 95.9,
      labelX: 50,
      labelY: 50,
    },
  },
  right: {
    16: {
      SVGComponent: AreaA_Van_Right,
      positionX: 61,
      positionY: 19.5,
      labelX: 50,
      labelY: 70,
    },
    14: {
      SVGComponent: AreaB_Van_Right,
      positionX: 61,
      positionY: 38,
      labelX: 50,
      labelY: 50,
    },
    13: {
      SVGComponent: AreaC_Van_Right,
      positionX: 42,
      positionY: 57.7,
      labelX: 50,
      labelY: 50,
    },
    11: {
      SVGComponent: AreaD_Van_Right,
      positionX: 42.5,
      positionY: 80,
      labelX: 50,
      labelY: 50,
    },
  },
};

/**
 * Mapowanie obszarów lakieru dla HATCHBACK
 *
 * Hatchback ma 3 obszary na każdym widoku (A, B, C)
 * A - najbardziej na górze
 * C - najbardziej na dole
 */
const hatchbackAreasMap: VehicleAreasMap = {
  left: {
    1: {
      SVGComponent: AreaA_Hatchback_Left,
      positionX: 41.1,
      positionY: 23,
      labelX: 80,
      labelY: 80,
    },
    3: {
      SVGComponent: AreaB_Hatchback_Left,
      positionX: 44,
      positionY: 47.7,
      labelX: 50,
      labelY: 50,
    },
    5: {
      SVGComponent: AreaC_Hatchback_Left,
      positionX: 51,
      positionY: 74.8,
      labelX: 50,
      labelY: 40,
    },
  },
  top: {
    21: {
      SVGComponent: AreaA_Hatchback_Center,
      positionX: 50,
      positionY: 16,
      labelX: 50,
      labelY: 50,
      scale: {
        mobile: 0.94,
      }
    },
    20: {
      SVGComponent: AreaB_Hatchback_Center,
      positionX: 50,
      positionY: 66.2,
      labelX: 50,
      labelY: 50,
      scale: {
        mobile: 0.97,
      }
    },
    10: {
      SVGComponent: AreaC_Hatchback_Center,
      positionX: 50.3,
      positionY: 97.2,
      labelX: 50,
      labelY: 50,
      scale: {
        mobile: 0.96,
      }
    },
  },
  right: {
    16: {
      SVGComponent: AreaA_Hatchback_Right,
      positionX: 59.4,
      positionY: 23.4,
      labelX: 10,
      labelY: 80,
    },
    14: {
      SVGComponent: AreaB_Hatchback_Right,
      positionX: 55.8,
      positionY: 47.7,
      labelX: 50,
      labelY: 50,
    },
    12: {
      SVGComponent: AreaC_Hatchback_Right,
      positionX: 48.9,
      positionY: 74.9,
      labelX: 50,
      labelY: 40,
    },
  },
};

/**
 * Pobiera mapowanie obszarów dla danego typu pojazdu
 */
export const getVehicleAreasMap = (vehicleType: VehicleType): VehicleAreasMap => {
  switch (vehicleType) {
    case 'sedan':
      return sedanAreasMap;
    case 'van':
      return vanAreasMap;
    case 'hatchback':
      return hatchbackAreasMap;
    default:
      return sedanAreasMap;
  }
};

/**
 * Pobiera definicję obszaru dla konkretnego typu pojazdu, widoku i pozycji
 */
export const getAreaDefinition = (
  vehicleType: VehicleType,
  view: VehicleView,
  position: AreaPosition,
): AreaDefinition | undefined => {
  const map = getVehicleAreasMap(vehicleType);
  return map[view][position];
};
